/**
 * Auth Store — Zustand store for authentication state
 * Tokens are stored in HttpOnly cookies (managed by server)
 * This store only manages user state and UI loading/error states
 */
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type {
  AuthStore,
  User,
  LoginWithEmailPayload,
  RegisterPayload,
  SignupWithInvitePayload,
} from "@/types/auth.types";
import { authService } from "@/lib/api/services/auth.service";
import { handle403Pivot } from "@/lib/auth/handle-403-pivot";
import { useOrgStore } from "./useOrgStore";

const STORAGE_KEYS = {
  USER: "user",
} as const;

const storage = {
  get: (key: string): string | null => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(key);
  },
  set: (key: string, value: string): void => {
    if (typeof window === "undefined") return;
    localStorage.setItem(key, value);
  },
  remove: (key: string): void => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(key);
  },
  getJSON: <T>(key: string): T | null => {
    const value = storage.get(key);
    if (!value) return null;
    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  },
  setJSON: <T>(key: string, value: T): void => {
    storage.set(key, JSON.stringify(value));
  },
};

function canPivot403(error: unknown): error is { id?: string; statusCode?: number } {
  if (!error || typeof error !== "object") return false;
  const candidate = error as { id?: unknown; statusCode?: unknown };
  return candidate.statusCode === 403 && typeof candidate.id === "string";
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,

  login: async (payload: LoginWithEmailPayload) => {
    storage.remove(STORAGE_KEYS.USER);
    set({ user: null, isAuthenticated: false, isLoading: true, error: null });
    try {
      useOrgStore.getState().clearOrg();
      await authService.loginWithEmail(payload);
      set({ isAuthenticated: true, isLoading: false });
      await get().fetchUser();
    } catch (error) {
      if (canPivot403(error) && handle403Pivot(error)) {
        set({ error: null, isLoading: false, isAuthenticated: false });
        throw error;
      }
      const message = error instanceof Error ? error.message : "Login failed";
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  register: async (payload: RegisterPayload) => {
    storage.remove(STORAGE_KEYS.USER);
    useOrgStore.getState().clearOrg();
    set({ isLoading: true, error: null });
    try {
      await authService.register(payload);
      storage.remove(STORAGE_KEYS.USER);
      useOrgStore.getState().clearOrg();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Registration failed";
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  signupWithInvite: async (payload: SignupWithInvitePayload) => {
    storage.remove(STORAGE_KEYS.USER);
    useOrgStore.getState().clearOrg();
    set({ user: null, isAuthenticated: false, isLoading: true, error: null });
    try {
      const response = await authService.signupWithInvite(payload);
      set({ isAuthenticated: true, isLoading: false, error: null });
      await get().fetchUser();
      return response.data?.redirect;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Invite signup failed";
      set({ error: message, isAuthenticated: false, isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authService.logout();
    } finally {
      storage.remove(STORAGE_KEYS.USER);
      useOrgStore.getState().clearOrg();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: false,
        error: null,
      });
    }
  },

  refreshTokens: async () => {
    try {
      await authService.refreshToken();
    } catch (error) {
      await get().logout();
      throw error;
    }
  },

  fetchUser: async () => {
    try {
      const response = await authService.getCurrentUser();
      const user = response.data;
      storage.setJSON(STORAGE_KEYS.USER, user);
      set({ user, isAuthenticated: true });
    } catch (error) {
      console.error("Failed to fetch user:", error);
      set({ user: null, isAuthenticated: false });
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  setLoading: (loading: boolean) => set({ isLoading: loading }),

  hydrate: async () => {
    // Guard: only hydrate once — prevents duplicate API calls
    // when multiple AuthGuards mount (e.g., (admin) layout → redirect → (org-setup) layout)
    const { isInitialized, isLoading } = get();
    if (isInitialized || isLoading) return;

    const cachedUser = storage.getJSON<User>(STORAGE_KEYS.USER);

    if (cachedUser) {
      set({ user: cachedUser, isAuthenticated: true, isLoading: true });
    } else {
      set({ isLoading: true });
    }

    try {
      const response = await authService.checkAuth();

      if (response) {
        const user = response.data;
        storage.setJSON(STORAGE_KEYS.USER, user);
        set({ user, isAuthenticated: true, isInitialized: true, isLoading: false });
      } else {
        storage.remove(STORAGE_KEYS.USER);
        set({ user: null, isAuthenticated: false, isInitialized: true, isLoading: false });
      }
    } catch {
      storage.remove(STORAGE_KEYS.USER);
      set({ user: null, isAuthenticated: false, isInitialized: true, isLoading: false });
    }
  },
}));

export const useAuth = () =>
  useAuthStore(
    useShallow((state) => ({
      user: state.user,
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      isInitialized: state.isInitialized,
      error: state.error,
    })),
  );

export const useAuthActions = () =>
  useAuthStore(
    useShallow((state) => ({
      login: state.login,
      register: state.register,
      signupWithInvite: state.signupWithInvite,
      logout: state.logout,
      refreshTokens: state.refreshTokens,
      fetchUser: state.fetchUser,
      clearError: state.clearError,
      hydrate: state.hydrate,
    })),
  );

export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthUser = () => useAuthStore((state) => state.user);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
export const useAuthInitialized = () => useAuthStore((state) => state.isInitialized);
